/* eslint-disable */

import ToastifyStyle from "@/styles/components/ToastifyStyle";

import TimeAgo from "javascript-time-ago";
import React from "react";
import { createRoot } from "react-dom/client";

import App from "./App";
import AppProviders from "./context/app-context";
import "./index.css";
import { installAuthErrorHandler } from "./utils/auth-session";

import en from "javascript-time-ago/locale/en.json";

import {
  IS_CLEARBITS_ENABLED,
  IS_GOOGLE_TAG_MANAGER_ENABLED,
  IS_LEADSY_ENABLED,
  IS_REDDIT_PIXEL_ENABLED,
} from "./constants";
import reportWebVitals from "./reportWebVitals";

TimeAgo.addDefaultLocale(en);
installAuthErrorHandler();

// Vite fires this event when a dynamically imported chunk fails to load
// (e.g., after a new deployment invalidates old chunk hashes while the
// user's tab was idle). A single page reload fetches the new HTML with
// the updated chunk URLs, resolving the issue.
window.addEventListener("vite:preloadError", event => {
  const RELOAD_KEY = "vite_preload_error_reload";
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();

  // Only auto-reload once per 30 seconds to prevent infinite reload loops
  if (!lastReload || now - Number(lastReload) > 30_000) {
    sessionStorage.setItem(RELOAD_KEY, String(now));
    window.location.reload();
  }
});

declare global {
  interface Window {
    rdt?: any;
  }
}

function loadScript(
  src: string,
  position: HTMLElement,
  id: string,
  attributes?: Record<string, string>,
) {
  const script = document.createElement("script");
  script.setAttribute("src", src);
  script.setAttribute("id", id);
  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
  }
  position.appendChild(script);
}

if (IS_GOOGLE_TAG_MANAGER_ENABLED) {
  loadScript("https://www.googletagmanager.com/gtag/js?id=G-XXMML34VE3", document.head, "gtag-js");

  loadScript("https://www.googletagmanager.com/gtm.js?id=GTM-5FFWN8BP", document.head, "gtm-js");
}

if (IS_CLEARBITS_ENABLED) {
  loadScript(
    "https://tag.clearbitscripts.com/v1/pk_278599a0eb92a908585e2bdd5c566ad4/tags.js",
    document.head,
    "clearbit-js",
    { referrerpolicy: "strict-origin-when-cross-origin" },
  );
}

if (IS_LEADSY_ENABLED) {
  loadScript("https://r2.leadsy.ai/tag.js", document.head, "leadsy-js", {
    async: "true",
    "data-pid": "rO37czz8SuBLEN0H",
    "data-version": "062024",
  });
}

if (IS_REDDIT_PIXEL_ENABLED) {
  // Initialize Reddit Pixel
  if (!window.rdt) {
    const rdt = function (...args: any[]) {
      if (rdt.sendEvent) {
        rdt.sendEvent.apply(rdt, args);
      } else {
        rdt.callQueue.push(args);
      }
    };

    rdt.callQueue = [] as any[];

    rdt.sendEvent = undefined as any;
    window.rdt = rdt;

    // Load Reddit Pixel script
    loadScript("https://www.redditstatic.com/ads/pixel.js", document.head, "reddit-pixel-js", {
      async: "true",
    });

    // Initialize with your pixel ID and track page visit
    window.rdt("init", "a2_fdh7c3b6ekuo");
    window.rdt("track", "PageVisit");
  }
}

/**
 * Dismiss the remix-splash overlay (injected by index.html) with a rich
 * multi-property ease-out. The overlay fades, floats upward, and blurs —
 * it "lifts away" to softly reveal the dashboard underneath, matching the
 * same motion language as the share app's entrance in reverse.
 */
function dismissRemixSplash() {
  const splash = document.getElementById("remix-splash");
  if (!splash) return;

  // Freeze all looping child animations (breathing, dots) so the entire
  // overlay moves as one frozen surface during the exit.
  splash.querySelectorAll("*").forEach(child => {
    (child as HTMLElement).getAnimations().forEach(a => a.cancel());
  });
  splash.style.animation = "none";

  // Multi-property ease-out: opacity + upward drift + gentle blur.
  // The overlay lifts away like a veil being drawn up, softly revealing
  // the dashboard underneath.
  const anim = splash.animate(
    [
      { opacity: 1, transform: "translateY(0) scale(1)", filter: "blur(0)" },
      { opacity: 0, transform: "translateY(-8px) scale(1.005)", filter: "blur(3px)" },
    ],
    {
      duration: 500,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      fill: "forwards",
    },
  );
  anim.onfinish = () => splash.remove();
}

async function startApp() {
  // Initalise app after the Meticulous recorder is ready, e.g.
  const container = document.getElementById("root") as HTMLElement;
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <AppProviders>
        <ToastifyStyle />
        <App />
      </AppProviders>
    </React.StrictMode>,
  );

  // Once React has painted, dismiss the remix transition splash (if present).
  // The rAF ensures we wait until the first React frame is on screen so the
  // user never sees a blank flash between the splash and the app UI.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      dismissRemixSplash();
    });
  });
}

startApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
